import type { ReactNode } from 'react';
import { Image, StyleSheet, View } from 'react-native';

import { Card } from '@/components/Card';
import { Text } from '@/components/Text';
import { colors, fontSizes, radii, spacing } from '@/constants/theme';
import type { OrderedProduct } from '@/types/voltr';

export type OrderSummaryRow = { label: string; value: ReactNode; testID?: string };

export type OrderSummaryCardProps = {
  product: Pick<OrderedProduct, 'title' | 'image_url'>;
  rows: OrderSummaryRow[];
  testID?: string;
};

/**
 * The order in one card (Figma "Order Placed"): the product with "Qty 1 -
 * Free", then label/value rows. Shared by the review step and the
 * confirmation, so what the athlete approves is what they see placed.
 */
export function OrderSummaryCard({ product, rows, testID }: OrderSummaryCardProps) {
  return (
    <Card variant="flat" padding={0} testID={testID}>
      <View style={styles.product}>
        {product.image_url ? (
          <Image source={{ uri: product.image_url }} style={styles.image} resizeMode="cover" />
        ) : (
          <View style={styles.image} />
        )}
        <View style={styles.productText}>
          <Text variant="bodyBold" color="deepGreen" numberOfLines={2} style={styles.title}>
            {product.title}
          </Text>
          <Text variant="caption" color="textMuted">
            Qty 1 - Free
          </Text>
        </View>
      </View>
      {rows.map((row) => (
        <View key={row.label} style={styles.row} testID={row.testID}>
          <Text variant="caption" color="textMuted">
            {row.label}
          </Text>
          <View style={styles.value}>{row.value}</View>
        </View>
      ))}
    </Card>
  );
}

/** A row's value: right-aligned bold lines (an address is several). */
export function SummaryLines({ lines }: { lines: string[] }) {
  return (
    <>
      {lines.map((line, i) => (
        <Text key={i} variant="bodyBold" color="text" align="right" style={styles.line}>
          {line}
        </Text>
      ))}
    </>
  );
}

const IMAGE = 56;

const styles = StyleSheet.create({
  product: { flexDirection: 'row', alignItems: 'center', gap: spacing.md, padding: spacing.base },
  image: { width: IMAGE, height: IMAGE, borderRadius: radii.base, backgroundColor: colors.surfaceSunken },
  productText: { flex: 1, gap: 2 },
  title: { fontSize: fontSizes.lg },
  row: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: spacing.base,
    paddingHorizontal: spacing.base,
    paddingVertical: spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.border,
  },
  value: { flex: 1 },
  line: { fontSize: fontSizes.sm },
});
